import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  readCookieConsent,
  writeCookieConsent,
  type CookieConsentPreferences,
  type CookieConsentState,
} from '@/lib/cookie-consent';

type CookieConsentStatus = 'pending' | 'decided';

interface CookieConsentContextValue {
  status: CookieConsentStatus;
  preferences: CookieConsentPreferences;
  isBannerVisible: boolean;
  isPreferencesOpen: boolean;
  setPreferencesOpen: (open: boolean) => void;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  updatePreferences: (preferences: CookieConsentPreferences) => void;
  openPreferences: () => void;
}

const defaultPreferences: CookieConsentPreferences = {
  analytics: false,
  marketing: false,
};

function toPreferences(state: CookieConsentState | null): CookieConsentPreferences {
  if (!state) return defaultPreferences;
  return {
    analytics: state.analytics,
    marketing: state.marketing,
  };
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

interface CookieConsentProviderProps {
  children: ReactNode;
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps): JSX.Element {
  const initialState = readCookieConsent();
  const [status, setStatus] = useState<CookieConsentStatus>(initialState ? 'decided' : 'pending');
  const [preferences, setPreferences] = useState<CookieConsentPreferences>(toPreferences(initialState));
  const [isPreferencesOpen, setPreferencesOpen] = useState(false);

  const persistPreferences = useCallback((next: CookieConsentPreferences) => {
    writeCookieConsent(next);
    setPreferences(next);
    setStatus('decided');
  }, []);

  const acceptAll = useCallback(() => {
    persistPreferences({ analytics: true, marketing: false });
    setPreferencesOpen(false);
  }, [persistPreferences]);

  const rejectNonEssential = useCallback(() => {
    persistPreferences({ analytics: false, marketing: false });
    setPreferencesOpen(false);
  }, [persistPreferences]);

  const updatePreferences = useCallback(
    (next: CookieConsentPreferences) => {
      persistPreferences(next);
      setPreferencesOpen(false);
    },
    [persistPreferences]
  );

  const openPreferences = useCallback(() => {
    setPreferencesOpen(true);
  }, []);

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      status,
      preferences,
      isBannerVisible: status === 'pending',
      isPreferencesOpen,
      setPreferencesOpen,
      acceptAll,
      rejectNonEssential,
      updatePreferences,
      openPreferences,
    }),
    [acceptAll, isPreferencesOpen, openPreferences, preferences, rejectNonEssential, status, updatePreferences]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('[useCookieConsent] Hook must be used inside CookieConsentProvider.');
  }
  return context;
}
